<script lang="ts">
  import type { GameServer } from "@/api";
  import { serverStatusHint, statusLabels } from "@/config";
  import { routeHref } from "@/router";
  import Button from "@/components/Button.svelte";
  import { serverStatePolicy } from "@/server-state-policy";
  const {
    server,
    retryBusy = false,
    onRetry,
  }: { server: GameServer; retryBusy?: boolean; onRetry: () => void } = $props();
  const policy = $derived(serverStatePolicy(server));
  const pending = $derived(policy.pending);
  const failed = $derived(policy.failed);
  const setupStage = $derived(policy.setupStage);
</script>

{#if pending || failed || server.status === "stopped" || server.status === "running"}
  <section
    class="state-notice"
    class:failed
    class:online={server.status === "running" && !failed}
    aria-label="Состояние сервера"
  >
    <div role={failed ? "alert" : "status"}>
      <strong
        >{failed ? "Не удалось выполнить операцию с сервером" : statusLabels[server.status]}</strong
      >
      <p>{serverStatusHint(server.status, server.queuePosition)}</p>
      {#if pending}<small
          >Статус обновляется автоматически. Повторно отправлять действие не нужно.</small
        >{/if}
    </div>

    {#if setupStage >= 0}
      <ol aria-label="Этапы подготовки сервера">
        {#each ["Ожидание установки", "Установка", "Запуск"] as label, index (label)}
          <li
            class:active={setupStage === index}
            aria-current={setupStage === index ? "step" : undefined}
          >
            {index + 1}. {label}
          </li>
        {/each}
      </ol>
    {/if}
    {#if failed}
      {#if server.lastError}<details>
          <summary>Техническая причина ошибки</summary>
          <pre>{server.lastError}</pre>
        </details>{/if}
      <div class="actions">
        <a class="ghost-button" href={routeHref("server-console", { id: server.id })}
          >Посмотреть консоль</a
        >
        <Button disabled={retryBusy} onclick={onRetry}
          >{retryBusy ? "Отправляем запрос…" : "Повторить запуск"}</Button
        >
      </div>
    {/if}
  </section>
{/if}

<style>
  .state-notice {
    margin-bottom: 1.5rem;
    padding: 1.2rem 1.5rem;
    border: 1px solid var(--line-strong);
    border-left: 3px solid var(--amber);
    background: var(--surface);
  }
  .state-notice.failed {
    border-left-color: var(--mars-light);
  }
  .state-notice.online {
    border-left-color: var(--green);
  }
  strong {
    color: var(--cream);
    font-size: 0.9rem;
  }
  p {
    margin: 0.5rem 0;
    color: var(--muted);
    font-size: 0.85rem;
    line-height: 1.6;
  }
  small {
    color: var(--muted);
    font-size: 0.78rem;
  }
  ol {
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem 1.5rem;
    padding: 0;
    margin: 1rem 0 0;
    list-style: none;
    color: var(--muted);
    font-size: 0.8rem;
  }
  li.active {
    color: var(--mars-light);
    font-weight: 600;
  }
  details {
    margin-top: 1rem;
    font-size: 0.8rem;
  }
  summary {
    cursor: pointer;
    color: var(--muted);
  }
  pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    color: var(--cream);
    line-height: 1.6;
  }
  .actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 1rem;
  }
  .actions a {
    text-decoration: none;
  }
  @media (max-width: 520px) {
    .state-notice {
      padding: 1rem;
    }
  }
</style>
